import React, { useEffect, useState } from 'react';
import { db } from '../../../firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { Users, Calendar, Award, Star, BookOpen, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { SUBJECT_TYPES, SubjectType, TeacherBook, TeacherItem } from '../../../models';

export const TeacherBookSlider: React.FC<{ books?: TeacherBook[]; singleBookName?: string; singleBookDesc?: string; singleBookImageUrl?: string }> = ({ 
    books, 
    singleBookName, 
    singleBookDesc, 
    singleBookImageUrl 
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    // Normalize books list
    const bookList: TeacherBook[] = books && books.length > 0 ? books : (singleBookName ? [{
        id: 'single-1',
        name: singleBookName,
        desc: singleBookDesc,
        imageUrl: singleBookImageUrl
    }] : []);

    useEffect(() => {
        if (isHovered || bookList.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % bookList.length);
        }, 3500);
        return () => clearInterval(timer);
    }, [bookList.length, isHovered]);

    if (bookList.length === 0) return null;

    const book = bookList[currentIndex] || bookList[0];

    const handlePrev = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + bookList.length) % bookList.length);
    };

    const handleNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % bookList.length);
    };

    return (
        <div 
            className="w-full pt-4 border-t border-indigo-950/30 select-none"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="flex items-center justify-between mb-2.5">
                <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest pl-0.5 flex items-center gap-1.5">
                    <BookOpen size={13} />
                    대표 교재 라인업 {bookList.length > 1 ? `(${currentIndex + 1}/${bookList.length})` : ''}
                </h5>
                {bookList.length > 1 && (
                    <div className="flex items-center gap-1.5">
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={handlePrev}
                                className="w-6 h-6 rounded-full bg-slate-900/80 hover:bg-indigo-600 active:scale-90 text-slate-300 hover:text-white flex items-center justify-center border border-indigo-950/40 transition-all cursor-pointer"
                                title="이전 교재"
                            >
                                <ChevronLeft size={13} />
                            </button>
                            <button
                                type="button"
                                onClick={handleNext}
                                className="w-6 h-6 rounded-full bg-slate-900/80 hover:bg-indigo-600 active:scale-90 text-slate-300 hover:text-white flex items-center justify-center border border-indigo-950/40 transition-all cursor-pointer"
                                title="다음 교재"
                            >
                                <ChevronRight size={13} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Active Book Card */}
            <div 
                key={book.id || currentIndex}
                className="bg-[#111A36]/60 border border-indigo-950/40 hover:border-indigo-500/30 rounded-2xl p-3.5 flex gap-4 items-center transition-all duration-300 shadow-md animate-in fade-in"
            >
                {book.imageUrl ? (
                    <div className="w-14 h-20 rounded-lg bg-slate-950 border border-indigo-950/50 overflow-hidden shrink-0 flex items-center justify-center shadow-lg">
                        <img 
                            src={book.imageUrl} 
                            alt={book.name} 
                            className="w-full h-full object-cover" 
                        />
                    </div>
                ) : (
                    <div className="w-14 h-20 rounded-lg bg-[#111A36]/80 border border-indigo-950/50 overflow-hidden shrink-0 flex items-center justify-center text-indigo-400/40">
                        <BookOpen size={24} />
                    </div>
                )}

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 font-bold text-[10px]">
                            교재 #{currentIndex + 1}
                        </span>
                        <p className="font-black text-slate-100 text-sm truncate">{book.name}</p>
                    </div>
                    {book.desc && (
                        <p className="text-xs text-slate-400 leading-relaxed line-clamp-2 whitespace-pre-line">
                            {book.desc}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

let cachedTeachers: TeacherItem[] | null = null;
let cachedUnsubscribe: (() => void) | null = null;
const cachedListeners = new Set<(list: TeacherItem[]) => void>();

const imageMemoryCache = new Set<string>();
export const preloadImage = (url?: string) => {
    if (!url || imageMemoryCache.has(url)) return;
    imageMemoryCache.add(url);
    const img = new Image();
    img.src = url;
};

const preloadTeacherImages = (list: TeacherItem[]) => {
    list.forEach((t) => {
        if (t.imageUrl) preloadImage(t.imageUrl);
        if (t.bookImageUrl) preloadImage(t.bookImageUrl);
        if (t.booksList) {
            t.booksList.forEach((b) => {
                if (b.imageUrl) preloadImage(b.imageUrl);
            });
        }
    });
};

export const subscribeTeachers = (callback: (list: TeacherItem[]) => void) => {
    cachedListeners.add(callback);
    
    if (cachedTeachers) {
        callback(cachedTeachers);
        preloadTeacherImages(cachedTeachers);
    }
    
    if (!cachedUnsubscribe) {
        const q = query(collection(db, 'teachers'), orderBy('createdAt', 'desc'));
        cachedUnsubscribe = onSnapshot(q, (snapshot) => {
            const list: TeacherItem[] = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() } as TeacherItem);
            });
            
            if (cachedTeachers && cachedTeachers.length > 0) {
                // Keep the existing randomized order but update the item data if changed
                const updatedList = cachedTeachers.map(prev => {
                    const fresh = list.find(l => l.id === prev.id);
                    return fresh || prev;
                });
                // Append any newly added teachers
                const newItems = list.filter(l => !cachedTeachers!.some(prev => prev.id === l.id));
                cachedTeachers = [...updatedList, ...newItems];
            } else {
                // First load: Shuffle
                cachedTeachers = [...list].sort(() => Math.random() - 0.5);
            }
            
            preloadTeacherImages(cachedTeachers!);
            cachedListeners.forEach(cb => cb(cachedTeachers!));
        }, (err) => {
            console.error('Error fetching teachers:', err);
            cachedUnsubscribe = null;
        });
    }

    return () => {
        cachedListeners.delete(callback);
    };
};

const getYoutubeEmbedUrl = (url?: string): string | null => {
    if (!url) return null;
    let videoId = '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
        videoId = match[2];
        return `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`;
    }
    return null;
};

const IntroTeachers: React.FC = () => {
    const [teachers, setTeachers] = useState<TeacherItem[]>(cachedTeachers || []);
    const [loading, setLoading] = useState(!cachedTeachers);
    const [selectedSubject, setSelectedSubject] = useState<SubjectType | '전체'>('전체');

    useEffect(() => {
        if (cachedTeachers) {
            setTeachers(cachedTeachers);
            setLoading(false);
        }

        const unsubscribe = subscribeTeachers((list) => {
            setTeachers(list);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Filter teachers based on selected subject tab
    const filteredTeachers = selectedSubject === '전체'
        ? teachers
        : teachers.filter(t => t.subject === selectedSubject);

    return (
        <div className="animate-in fade-in duration-700 bg-[#0B132B] min-h-screen text-slate-100 flex flex-col">
            {/* Custom scrollbar hiding styles */}
            <style dangerouslySetInnerHTML={{__html: `
                .scrollbar-none::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-none {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}} />
            {/* Hero Section */}
            <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-[#0B132B] to-[#111A36]/30 text-center shrink-0">
                <div className="max-w-5xl mx-auto space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 text-indigo-300 font-black text-[10px] uppercase tracking-wider border border-indigo-500/20">
                        <Star size={12} className="text-indigo-400" /> Professional Instructors
                    </div>
                    <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight break-keep">
                        성장을 주도하는 <br />
                        <span className="text-indigo-400">정성우학원 대표 강사진</span>
                    </h1>
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-400 font-medium max-w-2xl mx-auto break-keep">
                        탄탄한 실력과 지도 노하우를 바탕으로 <br />
                        학생들의 성취와 미래를 함께 책임지고 이끌어갑니다.
                    </p>
                </div>
            </section>

            {/* Content Section */}
            <section className="pb-24 px-4 md:px-8 max-w-6xl mx-auto w-full flex-1">
                {/* Subject Filter Tabs */}
                {!loading && teachers.length > 0 && (
                    <div className="flex flex-wrap items-center justify-center gap-3 mb-12 animate-in fade-in slide-in-from-top-4 duration-500">
                        {(['전체', ...SUBJECT_TYPES] as const).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setSelectedSubject(tab)}
                                className={`px-6 py-2.5 rounded-full text-sm font-black transition-all duration-300 border ${
                                    selectedSubject === tab
                                        ? 'bg-indigo-650 border-indigo-500 text-white shadow-lg shadow-indigo-600/20 scale-102'
                                        : 'bg-[#111A36]/30 border-indigo-950/40 text-slate-400 hover:text-white hover:border-indigo-500/20'
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="py-32 flex flex-col items-center justify-center space-y-4">
                        <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                        <p className="text-slate-400 font-medium text-sm">강사 정보를 로딩하고 있습니다...</p>
                    </div>
                ) : teachers.length === 0 ? (
                    <div className="py-32 text-center rounded-[2.5rem] bg-[#111A36]/20 border border-indigo-950/30">
                        <Users size={48} className="mx-auto text-slate-600 mb-4" />
                        <p className="text-slate-300 font-black text-lg">등록된 강사 정보가 없습니다.</p>
                        <p className="text-slate-500 text-sm font-medium mt-1">학원 관리자 대시보드에서 새로운 강사를 등록해 보세요.</p>
                    </div>
                ) : filteredTeachers.length === 0 ? (
                    <div className="py-24 text-center rounded-[2.5rem] bg-[#111A36]/20 border border-indigo-950/30 text-slate-500 font-medium">
                        선택하신 과목({selectedSubject})의 등록된 강사 정보가 없습니다.
                    </div>
                ) : (
                    <div className="space-y-12 animate-in fade-in duration-300">
                        {filteredTeachers.map((item) => (
                            <div
                                key={item.id}
                                className="bg-[#111A36]/20 border border-indigo-950/40 rounded-[2.5rem] p-6 md:p-8 flex flex-col md:flex-row gap-8 items-start hover:border-indigo-500/10 transition-all duration-350 shadow-lg text-left"
                            >
                                {/* Left Side: Teacher Photo with fixed proportion */}
                                <div className="w-full md:w-64 lg:w-72 shrink-0 flex flex-col items-center md:sticky md:top-24">
                                    <div className="w-full max-w-[280px] md:max-w-none aspect-[3/4] rounded-[2rem] overflow-hidden bg-slate-950 border border-indigo-950/40 shadow-md relative group/photo">
                                        {item.hideIntroPhoto ? (
                                            <div className="w-full h-full bg-gradient-to-br from-indigo-950/90 via-slate-900 to-indigo-900/60 flex flex-col items-center justify-center p-6 text-center select-none min-h-[280px]">
                                                <div className="w-20 h-20 rounded-full bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400 mb-3 shadow-inner">
                                                    <User size={40} />
                                                </div>
                                                <span className="text-sm font-black text-indigo-300 tracking-wider">정성우학원</span>
                                                <span className="text-xs font-bold text-slate-400 mt-1">{item.name} 선생님</span>
                                            </div>
                                        ) : (
                                            <img
                                                src={item.imageUrl}
                                                alt={item.name}
                                                className="w-full h-full object-cover object-top"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        )}
                                        {/* Representative Textbook Cover overlapping in the bottom-right corner */}
                                        {item.booksList && item.booksList.length > 0 && item.booksList[0].imageUrl && (
                                            <div className="absolute bottom-3 right-3 w-16 aspect-[3/4] rounded-lg overflow-hidden border-2 border-indigo-500/20 shadow-xl shadow-black/60 transform rotate-6 group-hover/photo:rotate-0 group-hover/photo:scale-105 transition-all duration-300 z-20 bg-slate-950">
                                                <img
                                                    src={item.booksList[0].imageUrl}
                                                    alt={item.booksList[0].name}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right Side: Profile Info (Name, Intro, Career) + Textbooks List */}
                                <div className="flex-1 space-y-6 w-full flex flex-col justify-between">
                                    {/* Name, Intro, Career */}
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    {item.subject && (
                                                        <span className="px-2.5 py-1 rounded-lg bg-indigo-500/10 text-indigo-400 text-xs font-black border border-indigo-500/20">
                                                            {item.subject}
                                                        </span>
                                                    )}
                                                    <h4 className="text-lg sm:text-xl md:text-2xl font-black text-slate-100">{item.name} 선생님</h4>
                                                </div>
                                                {item.youtubeUrl && (
                                                    <a
                                                        href={item.youtubeUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1.5 text-xs text-red-500 hover:text-red-400 font-black transition-colors bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 px-3.5 py-1.5 rounded-full cursor-pointer shadow-sm shadow-red-500/5 active:scale-98"
                                                    >
                                                        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                                                            <path d="M23.498 6.163c-.272-1.022-1.074-1.826-2.099-2.099C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.399.564C1.577 4.337.775 5.141.502 6.163 0 8.07 0 12 0 12s0 3.93.502 5.837c.272 1.022 1.074 1.826 2.099 2.099C4.456 20.5 12 20.5 12 20.5s7.544 0 9.399-.564c1.025-.273 1.827-1.077 2.099-2.099C24 15.93 24 12 24 12s0-3.93-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                        </svg>
                                                        OT 영상 보기
                                                    </a>
                                                )}
                                            </div>
                                            {item.intro && (
                                                <p className="text-sm text-indigo-400 font-bold mt-2.5 leading-relaxed">
                                                    "{item.intro}"
                                                </p>
                                            )}
                                        </div>
                                        {(item.career || getYoutubeEmbedUrl(item.youtubeUrl)) && (
                                            <div className={`grid grid-cols-1 ${getYoutubeEmbedUrl(item.youtubeUrl) ? 'lg:grid-cols-12' : ''} gap-6 border-t border-indigo-950/30 pt-4`}>
                                                {item.career && (
                                                    <div className={getYoutubeEmbedUrl(item.youtubeUrl) ? 'lg:col-span-7 space-y-2' : 'space-y-2'}>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-0.5 flex items-center gap-1.5">
                                                            <Award size={12} className="text-indigo-400" />
                                                            약력 및 경력
                                                        </p>
                                                        <p className="text-xs text-slate-350 font-medium leading-relaxed whitespace-pre-line">
                                                            {item.career}
                                                        </p>
                                                    </div>
                                                )}
                                                {getYoutubeEmbedUrl(item.youtubeUrl) && (
                                                    <div className="lg:col-span-5 space-y-2">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-0.5 flex items-center gap-1.5">
                                                            <svg className="w-3.5 h-3.5 fill-indigo-400" viewBox="0 0 24 24">
                                                                <path d="M23.498 6.163c-.272-1.022-1.074-1.826-2.099-2.099C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.399.564C1.577 4.337.775 5.141.502 6.163 0 8.07 0 12 0 12s0 3.93.502 5.837c.272 1.022 1.074 1.826 2.099 2.099C4.456 20.5 12 20.5 12 20.5s7.544 0 9.399-.564c1.025-.273 1.827-1.077 2.099-2.099C24 15.93 24 12 24 12s0-3.93-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                            </svg>
                                                            소개 및 OT 영상
                                                        </p>
                                                        <div className="w-full aspect-video rounded-2xl overflow-hidden bg-slate-950 border border-indigo-950/40 shadow-md">
                                                            <iframe
                                                                src={getYoutubeEmbedUrl(item.youtubeUrl)!}
                                                                title={`${item.name} 선생님 OT 영상`}
                                                                className="w-full h-full border-0"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                allowFullScreen
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Textbook Carousel Slider */}
                                    {((item.booksList && item.booksList.length > 0) || item.bookName) && (
                                        <TeacherBookSlider 
                                            books={item.booksList}
                                            singleBookName={item.bookName}
                                            singleBookDesc={item.bookDesc}
                                            singleBookImageUrl={item.bookImageUrl}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default IntroTeachers;
